import { useQuery } from "@tanstack/react-query";

// ── Pointer file shape ───────────────────────────────────────────────────

export type PointersFile = {
  publishedAt: string;
  /** planCode → hashed S3 key of that plan's hat-index file. */
  hatIndex: Record<string, string>;
  /** planCode → hashed S3 key of that plan's combined cluster+pathway index. */
  clusterPathwayIndexes?: Record<string, string>;
  /** planCode → hashed S3 key of that plan's combined courses+climbs+ladders index. */
  learningIndexes?: Record<string, string>;
  /** Hashed S3 key of the global perks-and-quirks file (not plan-scoped). */
  perksQuirks?: string;
  /** planCode → hashed S3 key of that plan's hat-themes (hat packs) index. */
  hatThemeIndexes?: Record<string, string>;
  /** Hashed S3 key of the global topics file. */
  topics?: string;
  /** Hashed S3 key of the global connectors (building-blocks) file. */
  connectors?: string;
  /** Hashed S3 key of the global connector-activities file. */
  connectorActivities?: string;
  /** Hashed S3 key of the global videos file (by-id lookup). */
  videos?: string;
  /** Hashed S3 key of the global awards file (every award + plan codes). */
  awards?: string;
  /** planCode → hashed S3 key of that plan's combined facets + facet-types index. */
  facetIndexes?: Record<string, string>;
  /** planCode → hashed S3 key of that plan's educator-downloads index. */
  educatorDownloadIndexes?: Record<string, string>;
  /** planCode → hashed S3 key of that plan's reports index. */
  reportIndexes?: Record<string, string>;
  /** planCode → hashed S3 key of that plan's badges index. */
  badgeIndexes?: Record<string, string>;
  /** Hashed S3 key of the global banners file. */
  banners?: string;
  /** Hashed S3 key of the global career-plan-next-steps file. */
  careerPlanNextSteps?: string;
  /** Hashed S3 key of the global CMS regions taxonomy file. */
  regions?: string;
  /** Hashed S3 key of the global products index file. */
  productIndex?: string;
  /** DynamoDB districtId → hashed S3 key of that district's settings file. */
  districtSettings?: Record<string, string>;
};

// ── CDN base URL + fetch helper ──────────────────────────────────────────

export const cdnBase = (): string => {
  const env = (
    import.meta as unknown as {
      env: Record<string, string | undefined>;
    }
  ).env;
  const url = env.VITE_CONTENT_CDN_URL;
  if (!url) {
    throw new Error(
      "VITE_CONTENT_CDN_URL is not set — static content hooks cannot resolve content",
    );
  }
  return url.replace(/\/$/, "");
};

export const fetchStaticJson = async <T>(path: string): Promise<T> => {
  const res = await fetch(`${cdnBase()}/${path}`);
  if (!res.ok) {
    throw new Error(`Fetch ${path} failed: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
};

// ── TanStack Query cache policies for the three artifact kinds ───────────

/**
 * Pointer / overlay files (e.g. `pointers.json`, `districts/<id>.json`).
 * Short stale time so editorial changes propagate quickly, long gcTime so
 * navigation across pages doesn't refetch.
 */
export const POINTER_RQ = {
  staleTime: 30_000,
  gcTime: 10 * 60_000,
};

/**
 * Content-hashed files (hat-index, hat detail, etc.). The filename changes
 * whenever the body changes, so once loaded a file is valid forever.
 */
export const IMMUTABLE_RQ = {
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: 7 * 24 * 60 * 60_000,
};

/**
 * Un-hashed region files (`regions/<id>.json`). Short cache to pick up
 * republished demand/payRange changes.
 */
export const REGION_RQ = {
  staleTime: 60_000,
  gcTime: 10 * 60_000,
};

// ── Shared pointer hook ──────────────────────────────────────────────────

/**
 * Fetch the published `pointers.json` from the configured CDN. All callers
 * across the app share a single TanStack Query cache entry (query key
 * `['s3','pointers']`), so we hit the network once per stale window.
 */
export const usePointers = (): {
  pointers?: PointersFile;
  isLoading: boolean;
  error: Error | null;
} => {
  const { data, isLoading, error } = useQuery<PointersFile>({
    queryKey: ["s3", "pointers"],
    queryFn: () => fetchStaticJson<PointersFile>("pointers.json"),
    ...POINTER_RQ,
  });
  return {
    pointers: data,
    isLoading,
    error: (error as Error | null) ?? null,
  };
};
